import { type FC, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useProcessingChecklist } from "@/shared/hooks/useProcessingChecklist";
import { ProcessingChecklist } from "@/shared/components/ProcessingChecklist";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

const CHECKLIST_ITEMS = [
  "Confirming who the court includes",
  "Checking who the court needs notified",
  "Building the sign-off list",
] as const;

export const LoadingScreen: FC<FlowCustomComponentProps> = ({ onComplete }) => {
  const { person } = useAppContext();
  const queryClient = useQueryClient();
  const estateId = person?.estate_id;
  const [recomputeDone, setRecomputeDone] = useState(false);
  const [recomputeFailed, setRecomputeFailed] = useState(false);
  const calledRef = useRef(false);

  useEffect(() => {
    if (!estateId || calledRef.current) return;
    calledRef.current = true;

    (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        const { error } = await supabase.functions.invoke("legal/probate/compute-signoffs", {
          body: { estateId },
          headers: session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : undefined,
        });

        if (error) {
          console.error("Compute signoffs failed:", error);
          setRecomputeFailed(true);
          setRecomputeDone(true);
          return;
        }

        await queryClient.invalidateQueries({ queryKey: ["person-contacts"] });
        setRecomputeDone(true);
      } catch (err) {
        console.error("Compute signoffs error:", err);
        setRecomputeFailed(true);
        setRecomputeDone(true);
      }
    })();
  }, [estateId, queryClient]);

  const { activeIndex, isDone } = useProcessingChecklist({
    itemCount: CHECKLIST_ITEMS.length,
    isTerminal: recomputeDone,
    failed: recomputeFailed,
    onComplete: () => onComplete(),
    onFailed: () => {
      enqueueSnackbar("Something went wrong building your list. The results may be incomplete.", { variant: "error" });
      onComplete();
    },
  });

  return (
    <div className="flex flex-1 flex-col items-center justify-center text-center -mt-8 min-h-[60vh]">
      <Loader2 className="size-12 animate-spin text-brand" />

      <h4 className="mt-6">Building your list for the court</h4>
      <p className="mt-2 text-sm text-secondary">This usually takes under a minute</p>

      <ProcessingChecklist
        items={CHECKLIST_ITEMS}
        activeIndex={activeIndex}
        isDone={isDone}
      />
    </div>
  );
};
